import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { ApiTriggerType } from 'api/types';
import SliderPercentage from 'components/basic/inputs/Slider/SliderPercentage';
import TextInputLabeled from 'components/basic/inputs/TextInputLabeled';
import { useTextCallbackThrottle } from 'components/hooks';
import TriggerTypeDropdown from 'components/trading/BuySellPanel/TriggerTypeDropdown';
import { formatNumber, formatNumber2 } from 'lib/display/formatting';
import { sanitizeAndTruncateNumber } from 'lib/numbers';
import { throttleInstance } from 'lib/throttle';

import {
  calculateTPSLPriceChangePercent,
  calculateTPSLTargetPriceFromPercentage,
} from './tpSlUtils';

const throttleUpdate = throttleInstance(400);

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px min-content;
  gap: 12px;
`;

const SLIDER_MIN = 1;
const SLIDER_MAX = 25;

export default function TPSLSliderAndInput({
  entryPrice,
  targetPrice,
  triggerBy,
  onChangePrice,
  targetAction,
  processing,
  trigger,
  triggerType,
  onChangeTriggerType,
  tickSizeDecimals,
  removeButton,
  initialMarginFraction,
}: {
  entryPrice: string | number;
  targetPrice: string;
  targetAction: 'buy' | 'sell';
  triggerBy: 'byChange' | 'byROI';
  onChangePrice: (price: string) => void;
  processing: boolean;
  triggerType: ApiTriggerType;
  trigger: 'takeProfit' | 'stopLoss';
  onChangeTriggerType: (type: ApiTriggerType) => void;
  tickSizeDecimals: number | undefined;
  removeButton: ReactNode;
  initialMarginFraction: string | undefined;
}) {
  // TP Price - Entry Price / Entry Price * IMF (For market and wallet)
  const priceChangePercent = useMemo(
    () =>
      calculateTPSLPriceChangePercent({
        entryPrice,
        targetAction,
        targetPrice,
        trigger,
        triggerBy,
        initialMarginFraction,
      }),
    [
      entryPrice,
      initialMarginFraction,
      targetAction,
      targetPrice,
      trigger,
      triggerBy,
    ]
  );

  const formatPercentageInput = (value: string | number) =>
    formatNumber2(value, 2, 1, false);

  const [inputPercentage, setInputPercentage] = useState<string>(
    priceChangePercent ? formatPercentageInput(priceChangePercent) : ''
  );

  const [sliderPercentage, setSliderPercentage] = useState<number>(
    priceChangePercent || 0
  );

  const handleDispatchPercentage = (percentage: number) => {
    if (!percentage) {
      // Reset on "0" or empty value
      onChangePrice('');
      return;
    }
    const targetPrice = calculateTPSLTargetPriceFromPercentage({
      entryPrice,
      targetAction,
      targetPercentage: percentage,
      trigger,
      triggerBy,
      initialMarginFraction,
    });
    onChangePrice(
      formatNumber(targetPrice, {
        fixedDecimals: tickSizeDecimals,
        thousandsSeparator: false,
      })
    );
  };

  const handleChangeSliderPercentage = (percentageUpdate: number) => {
    setSliderPercentage(percentageUpdate);
    setInputPercentage(formatPercentageInput(percentageUpdate));
    throttleUpdate(() => {
      handleDispatchPercentage(percentageUpdate);
    });
  };

  const handleChangeInputPercentage = (percentageUpdate: string) => {
    setInputPercentage(percentageUpdate);
    const percentageOrNaN = Number(percentageUpdate);
    if (isNaN(percentageOrNaN)) {
      setSliderPercentage(SLIDER_MIN);
      return;
    }
    setSliderPercentage(percentageOrNaN);
    throttleUpdate(() => {
      handleDispatchPercentage(percentageOrNaN);
    });
  };

  // Sync from parent
  const [isDragging, setIsDragging] = useState(false);
  const [isEnteringPercentage, setIsEnteringPercentage] = useState(false);

  useTextCallbackThrottle(
    `${priceChangePercent || ''}`,
    useCallback(
      (value: string) => {
        if (!isDragging) {
          setSliderPercentage(value ? Number(value) : 0);
        }
        if (!isEnteringPercentage) {
          setInputPercentage(formatPercentageInput(value));
        }
      },
      [isDragging, isEnteringPercentage]
    ),
    500
  );

  return (
    <>
      <InputGrid>
        <div>
          <TextInputLabeled
            numbericInputMode
            label={
              <TriggerTypeDropdown
                selected={triggerType}
                onChange={onChangeTriggerType}
              />
            }
            disabled={processing}
            onChange={value =>
              onChangePrice(sanitizeAndTruncateNumber(value, tickSizeDecimals))
            }
            value={targetPrice}
          />
        </div>
        <TextInputLabeled
          label="%"
          onChange={handleChangeInputPercentage}
          value={inputPercentage}
          noMinWidth
          onFocusIn={() => setIsEnteringPercentage(true)}
          onFocusOut={() => setIsEnteringPercentage(false)}
        />
        <div>{removeButton}</div>
      </InputGrid>
      <SliderPercentage
        onChange={handleChangeSliderPercentage}
        value={Math.min(Math.round(sliderPercentage), SLIDER_MAX)}
        min={SLIDER_MIN}
        max={SLIDER_MAX}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => setIsDragging(false)}
        hiddenLabel
        quickSelectIncludeMin={false}
      />
      {/* <SliderPercentage
        onChange={handleChangeSliderPercentage}
        value={Math.min(Math.round(sliderPercentage), SLIDER_MAX)}
        min={SLIDER_MIN}
        max={SLIDER_MAX}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => setIsDragging(false)}
      /> */}
    </>
  );
}
